/**
 * Utility class for handling video URLs and embedding
 */
class VideoHelper {
  /**
   * Extract YouTube video ID from various YouTube URL formats
   * Supports:
   * - https://www.youtube.com/watch?v=VIDEO_ID
   * - https://youtu.be/VIDEO_ID
   * - https://www.youtube.com/embed/VIDEO_ID
   * - https://www.youtube.com/v/VIDEO_ID
   */
  static getYouTubeVideoId(url: string): string | null {
    if (!url) return null

    try {
      const urlObj = new URL(url)
      
      // Standard youtube.com URLs
      if (urlObj.hostname.includes('youtube.com')) {
        // Check for /watch?v= format
        const videoId = urlObj.searchParams.get('v')
        if (videoId) return videoId
        
        // Check for /embed/ or /v/ format
        const pathMatch = urlObj.pathname.match(/\/(embed|v)\/([^/?]+)/)
        if (pathMatch) return pathMatch[2]
      }
      
      // Short youtu.be URLs
      if (urlObj.hostname === 'youtu.be') {
        const videoId = urlObj.pathname.substring(1).split('/')[0]
        if (videoId) return videoId
      }
      
      return null
    } catch {
      return null
    }
  }

  /**
   * Get YouTube embed URL from a video URL
   */
  static getYouTubeEmbedUrl(url: string): string | null {
    const videoId = this.getYouTubeVideoId(url)
    if (!videoId) return null
    
    return `https://www.youtube.com/embed/${videoId}`
  }

  /**
   * Check if URL is a valid YouTube URL
   */
  static isYouTubeUrl(url: string): boolean {
    return this.getYouTubeVideoId(url) !== null
  }
}

export default VideoHelper
